import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CalloutUnitRenderer } from '@buzzfeed/react-components';
import { ABEAGLE_FLIPPER_PARAM } from '@buzzfeed/react-components/lib/utils/abeagle';
import { isNews } from '../../utils/isNews';
import { isSponsored } from '../../utils/isSponsored';
import ABeagleContext from '../../contexts/abeagle';
import StickyContext from '../../contexts/sticky';
import { CLUSTER } from '../../constants';
import EXPERIMENTS from '../../constants/ab-tests';
/**
 * TODO: Evaluate whether moving these tracking functions into CalloutUnitRenderer
 * would be a better architectural choice.
 */
import {
  trackClientContentAction,
  trackClientExternalLink,
  trackClientImpression,
  trackClientInternalLink,
} from '../../hooks/analytics/client-event-tracking';

/**
 * Determine page eligibility for Callout Unit
 * @param {*} buzz
 * @returns
 */
const isEligible = (buzz) => (
  buzz.isUS &&
  (typeof window !== 'undefined' && !window.location.search.includes('origin=sdxa')) &&
  !isNews(buzz) &&
  !isSponsored(buzz) &&
  !(buzz?.laser_tags?.bf_content_description?.workflow || []).some(
    tag => tag.tag_name === 'generator_generator'
  )
);

const getExperimentPayload = (experiment) => {
  if (experiment?.variant?.payload) {
    try {
      const payload = JSON.parse(experiment.variant.payload);
      if (payload && typeof payload === 'object' && payload !== null) {
        // If the payload is an object, we assume it contains overrides
        return { ...payload };
      }
    } catch (error) {
      if (CLUSTER !== 'prod') {
        console.warn('Error parsing experiment payload:', error);
      }
    }
  }
  return {};
};

const getDebugSuppressionExperiments = () => {
  const queryParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );

  if (!queryParams.has(ABEAGLE_FLIPPER_PARAM)) {
    return [];
  }

  return EXPERIMENTS.filter(experiment =>
    experiment.hideCalloutForVariants && queryParams.has(`abeagle_${experiment.name}`)
  );
};

/**
 * A memoized React component that conditionally renders a CalloutUnitRenderer
 * based on the A/B test variant asigned to client and adjusts for a sticky header offset.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.buzz - BuzzFeed analytics context for tracking events.
 * @param {string} props.pageData
 * @param {string} props.siteCaptchaKey - Site captcha key for the callout unit forms.
 * @returns {JSX.Element|null} The CalloutUnitRenderer with applied overrides or null if no variant.
 */
const CalloutUnitExperiment = memo(function CalloutWithExperiment({
  buzz,
  pageData,
  siteCaptchaKey,
}) {
  const { experiments, getExperimentValue } = useContext(ABeagleContext);
  const [experimentVariant, setExperimentVariant] = useState(null);
  const { getTopmostPosition, subscribe, unsubscribe } = useContext(StickyContext);
  const [offsetTop, setOffsetTop] = useState(0);

  const offsets = useMemo(() => ({ top: offsetTop }), [offsetTop]);

  /**
   * Determine the experiment and variant for CalloutUnit:
   * - Searches the ABeagle context for an active callout-unit experiment.
   * - Checks for ABeagle debug or override parameters in the URL, allowing manual selection of
   *   variants.
   * - Returns an object with the experiment name and variant, excluding null or undefined values.
   */
  const experiment = useMemo(() => {
    const name_prefix = 'callout-unit';

    const queryParams = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    for (const key of queryParams.keys()) {
      if (
        queryParams.has(ABEAGLE_FLIPPER_PARAM) &&
        key.toLowerCase().includes(`${name_prefix}`)
      ) {
        return { name: key.replace(/^abeagle_/, '') };
      }
    }

    const [name, variant] = Object.entries(experiments?.eligible || {}).find(
      ([experimentName, eligibleVariant]) => (
        experimentName.toLowerCase().includes(name_prefix) &&
        // preserve falsy values like 0, "", and false while only rejecting null and undefined.
        (eligibleVariant?.value ?? null) !== null
      )
    ) || [null, {}];

    return { name, variant };
  }, [experiments]);

  const suppressionExperiments = useMemo(() => {
    const debugExperiments = getDebugSuppressionExperiments();
    if (debugExperiments.length) {
      return debugExperiments.map(experiment => ({
        name: experiment.name,
        variant: undefined,
      }));
    }

    return EXPERIMENTS.filter(config => {
      const eligibleVariant = experiments?.eligible?.[config.name];
      return config.hideCalloutForVariants && (eligibleVariant?.value ?? null) !== null;
    }).map(experiment => ({
      name: experiment.name,
      variant: experiments?.eligible?.[experiment.name],
    }));
  }, [experiments]);

  // Overrides for the Callout Unit, including the experiment variant that might be provided by
  // ABeagle. Starts as null until experiment state is determined.
  const overrides = useMemo(
    () => {
      // If experiment is not loaded yet or we haven't determined the variant, return null
      if (!experiments?.loaded || (experiment.name && experimentVariant === null)) {
        return null;
      }

      // If no experiment or no variant, return empty object
      if (!experimentVariant) {
        return {};
      }

      // If the experiment has a payload, we use it as overrides.
      return getExperimentPayload(experiment);
    },
    [experiment, experimentVariant, experiments?.loaded]
  );

  const track = useMemo(() => ({
    contentAction: data => trackClientContentAction(buzz, data),
    externalLink: data => trackClientExternalLink(buzz, data),
    impression: data => trackClientImpression(buzz, data),
    internalLink: data => trackClientInternalLink(buzz, data),
  }), [buzz]);

  // This effect retrieves the experiment variant from the A/B testing context
  // and updates the component's state. It only runs when the experiment name
  // or the function to get the experiment value changes.
  useEffect(() => {
    if (!experiment.name) {
      return;
    }

    const variant = getExperimentValue(experiment.name, { rejectErrors: false });
    if (variant) {
      setExperimentVariant(variant);
    }
  }, [getExperimentValue, experiment]);

  // This effect subscribes to the sticky header's position updates. It sets
  // the vertical offset for the callout unit so it doesn't overlap with
  // the sticky header. It cleans up the subscription on unmount.
  useEffect(() => {
    const top = getTopmostPosition();
    if (!top || typeof top !== 'number' || !isFinite(top)) {
      return;
    }

    const handleUpdate = () => setOffsetTop(top);
    subscribe(handleUpdate);

    return () => {
      unsubscribe(handleUpdate);
    };
  }, [getTopmostPosition, subscribe, unsubscribe]);

  const memoizedPageData = useMemo(() => {
    if (experiment.name && experimentVariant !== null) {
      return {
        ...pageData,
        abeagleExperiment: {
          name: experiment.name,
          variant: experimentVariant,
          payload: getExperimentPayload(experiment),
        },
      };
    }
    return { ...pageData };
  }, [pageData, experiment, experimentVariant]);

  /**
    * Check if the callout should be hidden for the current suppression experiment variant.
    * Uses raw eligible.value when available so that unbucketed users (value === null)
    * are not suppressed, and falls back to getExperimentValue for abdebug overrides.
   */
  const shouldHideCallout = useMemo(() => {
    if (!suppressionExperiments.length) {
      return false;
    }

    return suppressionExperiments.some(suppressionExperiment => {
      const suppressionVariantValue = suppressionExperiment.variant?.value
        ?? getExperimentValue(suppressionExperiment.name, { rejectErrors: false });

      const config = EXPERIMENTS.find(e => e.name === suppressionExperiment.name);
      return config?.hideCalloutForVariants?.includes(suppressionVariantValue) ?? false;
    });
  }, [getExperimentValue, suppressionExperiments]);

  if (!isEligible(buzz)) {
    return null;
  }

  if (!experiments?.loaded || overrides === null) {
    return null;
  }

  if (shouldHideCallout) {
    return null;
  }

  // Pass the variant and dynamic offset as overrides
  return (
    <CalloutUnitRenderer
      cluster={CLUSTER}
      offsets={offsets}
      overrides={overrides}
      pageData={memoizedPageData}
      siteCaptchaKey={siteCaptchaKey}
      track={track}
    />
  );
});

CalloutUnitExperiment.propTypes = {
  pageData: PropTypes.shape({
    context_page_id: PropTypes.string.isRequired,
    context_page_type: PropTypes.string.isRequired,
    page_edition: PropTypes.string.isRequired,
    referrer_uri: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    sections: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  siteCaptchaKey: PropTypes.string.isRequired,
};

export default CalloutUnitExperiment;
